/**
 * Traducao dos codigos de erro do backend para frases (porte de `frontend/lib/errors.py`).
 *
 * O backend responde `detail` com um CODIGO estavel (`client_has_reports`), nao com uma frase. E a
 * escolha certa: a API nao decide a lingua nem o tom da interface. Mas isso so vira ajuda se o
 * dicionario existir do lado de ca - sem ele a tela mostra `dataset_connection_mismatch` para quem
 * esta tentando publicar um relatorio.
 *
 * **Codigo desconhecido volta cru, de proposito.** Esconde-lo atras de "erro inesperado" apagaria a
 * unica pista util quando o backend ganhar um codigo novo e este arquivo ficar para tras.
 *
 * SEGUNDA copia deste dicionario (a primeira e a do Streamlit, em `frontend/lib/errors.py`). Elas
 * precisam andar juntas enquanto os dois frontends existirem; quando o Streamlit sair, esta fica.
 */

import { ApiError } from '../api/client'

const MENSAGENS: Record<string, string> = {
  // --- nao encontrado
  client_not_found: 'Cliente nao encontrado.',
  hospital_not_found: 'Hospital nao encontrado.',
  powerbi_connection_not_found: 'Conexao PowerBI nao encontrada.',
  dataset_not_found: 'Dataset nao encontrado.',
  report_not_found: 'Report nao encontrado.',
  report_page_not_found: 'Pagina de report nao encontrada.',
  access_policy_not_found: 'Policy nao encontrada.',
  access_rule_not_found: 'Regra nao encontrada.',
  unknown_entity: 'Tipo de registro desconhecido.',
  // --- integridade referencial (bloqueiam exclusao)
  client_has_hospitals: 'Este cliente tem hospitais vinculados. Exclua os hospitais primeiro.',
  client_has_powerbi_connections:
    'Este cliente tem conexoes PowerBI vinculadas. Exclua as conexoes primeiro.',
  client_has_datasets: 'Este cliente tem datasets vinculados. Exclua os datasets primeiro.',
  client_has_reports: 'Este cliente tem reports vinculados. Exclua os reports primeiro.',
  client_has_access_policies:
    'Este cliente tem RLS policies vinculadas. Exclua as policies primeiro.',
  hospital_has_reports: 'Este hospital tem reports vinculados. Exclua ou realoque os reports primeiro.',
  powerbi_connection_has_reports: 'Esta conexao tem reports vinculados. Exclua os reports primeiro.',
  powerbi_connection_has_datasets:
    'Esta conexao tem datasets vinculados. Exclua os datasets primeiro.',
  dataset_has_reports: 'Este dataset tem reports vinculados. Exclua os reports primeiro.',
  report_has_access_policies: 'Este report tem RLS policies vinculadas. Exclua as policies primeiro.',
  // --- duplicidade
  client_mapping_already_exists: 'Este tenant ja esta mapeado como cliente.',
  hospital_mapping_already_exists: 'Esta organization ja esta mapeada como hospital.',
  powerbi_connection_already_exists: 'Ja existe uma conexao com esta chave (connection_key).',
  dataset_already_exists: 'Ja existe um dataset com estes dados.',
  report_already_exists: 'Ja existe um report com estes dados.',
  access_policy_already_exists: 'Ja existe uma policy com estes dados.',
  report_profile_rls_policy_already_exists: 'Ja existe uma policy RLS para este report e profile.',
  // --- coerencia entre entidades
  hospital_client_mismatch: 'O hospital pertence a outro cliente.',
  powerbi_connection_client_mismatch: 'A conexao PowerBI pertence a outro cliente.',
  dataset_client_mismatch: 'O dataset pertence a outro cliente.',
  dataset_connection_mismatch: 'O dataset usa uma conexao PowerBI diferente da escolhida.',
  dataset_powerbi_workspace_mismatch: 'O workspace do PowerBI difere do workspace do dataset.',
  report_client_mismatch: 'O report pertence a outro cliente.',
  rls_report_dataset_target_mismatch: 'A policy aponta para um dataset diferente do report.',
  rls_report_requires_dataset: 'O report precisa estar ligado a um dataset para ter RLS.',
  cross_tenant_access_denied: 'Operacao entre tenants diferentes nao e permitida.',
  // --- mudancas bloqueadas por dependencia
  dataset_powerbi_ids_change_requires_report_removal:
    'Este dataset tem reports vinculados: nao da para trocar o workspace/dataset do PowerBI sem excluir os reports antes (o embed deles aponta para esses ids).',
  report_dataset_change_requires_policy_removal:
    'Este report tem RLS policies: exclua as policies antes de trocar o dataset.',
  rls_rule_cannot_be_deleted_delete_policy_instead:
    'Para trocar a role, exclua a policy e crie outra.',
  // --- publicacao
  pbi_report_requires_connection: 'Report PowerBI exige uma conexao.',
  pbi_report_requires_dataset_workspace_report_embed_for_publication:
    'Para publicar um report PowerBI faltam dados: conexao, dataset, workspace, report id e embed URL.',
  published_report_requires_compatible_connection_dataset:
    'A conexao e o dataset do report precisam ser compativeis para publicar.',
  report_url_required_for_publication: 'Report sem PowerBI exige a URL de render para publicar.',
  // --- RLS
  mvp_requires_user_id_rls_policy: 'No MVP so existe policy RLS com identidade user_id.',
  mvp_requires_rls_policy: 'So existe policy do tipo RLS.',
  unsupported_identity_source: 'Fonte de identidade invalida: use e-mail ou user_id.',
  rls_identity_source_unsupported:
    'A policy deste report usa uma fonte de identidade que este Viewer nao conhece. Atualize o Viewer ou corrija a policy.',
  rls_policy_requires_exactly_one_role: 'Uma policy RLS tem exatamente uma role.',
  rls_policy_requires_at_least_one_role: 'Informe ao menos uma role para a policy.',
  rls_role_already_in_policy: 'Esta role ja esta na policy (nao repita).',
  rls_multiple_organizations:
    'Este usuario pertence a mais de uma organizacao e a policy filtra por organizacao. Use outra fonte de identidade ou ajuste as organizacoes do usuario no Auth.',
  profile_has_no_workspaces: 'Este profile nao alcanca nenhum workspace no Auth.',
  workspace_not_in_profile: 'O workspace do report nao esta no profile escolhido.',
  // --- Auth / upstream
  tenant_not_onboarded: 'Este tenant ainda nao foi mapeado como cliente.',
  auth_tenant_not_found: 'Tenant nao encontrado no Auth.',
  auth_organization_not_found: 'Organization nao encontrada no Auth.',
  auth_workspace_not_found: 'Workspace nao encontrado no Auth.',
  auth_profile_not_found: 'Profile nao encontrado no Auth.',
  auth_workspace_organization_mismatch: 'O workspace nao pertence a essa organization.',
  auth_service_unavailable: 'Auth indisponivel no momento.',
  auth_operational_token_not_configured: 'Token operacional do Auth nao configurado (.env).',
  auth_login_failed: 'E-mail ou senha invalidos.',
  admin_access_denied: 'Seu usuario nao tem permissao de admin do Report Manager.',
  // --- Auth Reader com app api key (spec 0009)
  resource_action_forbidden: 'Seu perfil nao alcanca esta parte do Report Manager. Peca a permissao a quem administra o Auth.',
  app_resource_mismatch: 'A chave de aplicacao configurada neste servidor aponta para outra aplicacao. E erro de configuracao, nao de credencial.',
  auth_app_api_key_not_configured: 'A chave de aplicacao nao esta configurada neste servidor.',
  invalid_api_key: 'A chave de aplicacao foi recusada pelo Auth: invalida, expirada ou cancelada.',
  global_admin_required: 'Esta leitura exige um administrador Cross Tenant no Auth.',
  resource_not_found: 'O resource nao existe no Auth. Ele precisa ser registrado antes de a permissao valer.',
  invalid_admin_session: 'Sessao invalida. Entre novamente.',
  missing_token: 'Sessao ausente. Entre novamente.',
  // --- PowerBI
  powerbi_auth_failed: 'Falha ao autenticar no PowerBI (verifique o service principal).',
  powerbi_access_denied: 'O service principal nao tem acesso a esse workspace do PowerBI.',
  powerbi_report_not_found: 'Report nao encontrado no PowerBI.',
  powerbi_connection_not_configured: 'Conexao nao configurada no .env (POWERBI_CONNECTIONS_JSON).',
  powerbi_connection_credentials_incomplete: 'Credenciais da conexao PowerBI incompletas.',
  powerbi_upstream_error: 'O PowerBI retornou um erro.',
  invalid_powerbi_link: 'Link do PowerBI invalido.',
  group_id_and_report_id_required: 'Informe o link ou o groupId e o reportId.',
}

/** Rotulos das dependencias que aparecem no aviso de exclusao. */
export const ROTULOS_DE_DEPENDENCIA: Record<string, string> = {
  hospitals: 'hospital(is)',
  powerbi_connections: 'conexao(oes) PowerBI',
  datasets: 'dataset(s)',
  reports: 'report(s)',
  access_policies: 'RLS policy(ies)',
}

function isRecord(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null
}

/** Erro de validacao do pydantic: `[{loc: ['body','name'], msg: '...'}]`. */
function doPydantic(detail: unknown[]): string | null {
  const primeiro = detail[0]
  if (!isRecord(primeiro)) return null
  const loc = Array.isArray(primeiro.loc) ? primeiro.loc : []
  const campo = loc.filter((parte) => parte !== 'body').join('.')
  const mensagem = String(primeiro.msg ?? 'valor invalido')
  return campo === '' ? mensagem : `${campo}: ${mensagem}`
}

/**
 * Converte o erro numa frase. Nao troca a falha de rede por uma frase generica: quando a chamada
 * nem chega a ter resposta, dizer isso e mais util do que "ocorreu um erro".
 */
export function humanizar(erro: unknown): string {
  if (erro instanceof ApiError) {
    if (Array.isArray(erro.payload)) return doPydantic(erro.payload) ?? erro.detail
    return MENSAGENS[erro.detail] ?? erro.detail
  }
  if (erro instanceof Error) return erro.message
  return String(erro)
}
